import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShoppingCart, Trash2, Plus, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const resolveImg = (src) => {
  if (!src) return '/placeholder-package.jpg';
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  if (src.startsWith('/')) return `${BACKEND_URL}${src}`;
  return src;
};

const Cart = () => {
  const navigate = useNavigate();
  const { items, removeItem, total, count } = useCart();

  const goToPayment = () => {
    if (count === 0) return;
    // Marca o checkout como modo carrinho. CheckoutStep4 lerá daqui.
    sessionStorage.setItem('cartCheckout', JSON.stringify({ items, total, count }));
    sessionStorage.removeItem('bookingData');
    navigate('/checkout/pagamento');
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-7 h-7 text-theme-primary" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Meu Carrinho</h1>
          </div>
          <Link to="/pacotes" className="text-sm text-theme-primary hover:underline inline-flex items-center gap-1" data-testid="cart-add-more">
            <Plus className="w-4 h-4" /> Adicionar mais pacotes
          </Link>
        </div>

        {count === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Seu carrinho está vazio</h2>
              <p className="text-gray-500 mb-6">Adicione pacotes durante o checkout para comprar mais de uma viagem ao mesmo tempo.</p>
              <Button onClick={() => navigate('/pacotes')} className="btn-primary-theme" data-testid="cart-empty-cta">
                Ver pacotes disponíveis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                Todos os contratos abaixo já foram assinados. Confira os pacotes e siga para o pagamento único.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              {items.map((it) => (
                <Card key={it.id} data-testid={`cart-item-${it.id}`}>
                  <CardContent className="p-4 md:p-5">
                    <div className="flex gap-4">
                      <img
                        src={resolveImg(it.packageImage)}
                        alt={it.packageName}
                        className="w-24 h-24 md:w-28 md:h-28 rounded-lg object-cover flex-shrink-0"
                        onError={(e) => { e.target.src = '/placeholder-package.jpg'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="font-bold text-gray-900 truncate">{it.packageName}</h3>
                            <p className="text-sm text-gray-500 mt-0.5">{it.dateLabel}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{it.travelersCount} viajante(s)</p>
                            {it.contractAccepted && (
                              <span className="inline-flex items-center gap-1 mt-2 text-xs text-green-700">
                                <CheckCircle className="w-3 h-3" /> Contrato assinado
                              </span>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-theme-primary">{formatPrice(it.totalPrice)}</p>
                            <button
                              onClick={() => removeItem(it.id)}
                              className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                              data-testid={`cart-remove-${it.id}`}
                            >
                              <Trash2 className="w-3 h-3" /> Remover
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Total ({count} {count === 1 ? 'pacote' : 'pacotes'})</span>
                  <span className="text-2xl font-bold text-theme-primary" data-testid="cart-total">{formatPrice(total)}</span>
                </div>
                <p className="text-xs text-gray-500 mb-6">
                  Descontos promocionais e cupons (quando aplicáveis) serão calculados na próxima etapa.
                </p>
                <Button
                  onClick={goToPayment}
                  className="w-full btn-primary-theme min-h-[52px] font-bold text-base"
                  data-testid="cart-go-to-payment"
                >
                  Ir para Pagamento <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Cart;
